import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import {camelCase} from 'lodash';
import {DEFAULT_CURRENCY} from '@contentlab/views/contentlab/pricing/constants';
import AnalyticsService from '@/services/analytics/AnalyticsService';
import Sentry from '@/services/sentry/Sentry';
import {FLOW_NAMES} from '@/lib/analytics/analyticsConstants';
import {
  setContentLabWebPaymentFlowId,
  setContentLabWebPaymentIds,
} from '@/utils/LtidStorage';
import translate from '@/utils/translate';
import {
  Consumable,
  ContentLabPlan,
} from '@/types/models/pricing/contentLabPlan';
import ContentLabPlanBundle, {
  BUNDLE_TO_VARIANT,
} from '@/types/models/pricing/contentLabPlanBundle';
import {ContentLabSubscription} from '@/types/models/pricing/contentLabSubscription';
import Duration from '@/types/models/pricing/duration';
import {PlansCollection} from '@/types/models/pricing/plansCollection';
import {SubscriptionPlan} from '@/types/models/pricing/subscriptionPlan';

dayjs.extend(duration);

export const CONSUMABLE_NAME = 'Popular_pays_content_studio_video';

// The consumable type is sent as a key so it's deserialized to camelCase
export const CONSUMABLE_BALANCE_KEY = camelCase(CONSUMABLE_NAME);

export const getTokenBalance = (subscription?: ContentLabSubscription) =>
  subscription?.balance?.[CONSUMABLE_BALANCE_KEY].quantity;

export const getTokenRenewalDate = (subscription?: ContentLabSubscription) =>
  subscription?.renewAtMs ? new Date(subscription?.renewAtMs) : null;

export const getPlanTokenQuantity = (plan?: ContentLabPlan) =>
  plan?.consumables?.find(
    (c: Consumable) =>
      c.consumableType === CONSUMABLE_NAME && c.isAutoRenewing === true
  )?.quantity;

export const getPlanVariant = (subscription?: ContentLabSubscription) => {
  const bundle = subscription?.plan.quoteDetails.bundle ?? '';
  const isTrial = subscription?.isTrialPeriod;
  if (isTrial) return ContentLabPlanBundle.TRIAL;
  if (bundle in BUNDLE_TO_VARIANT) {
    return BUNDLE_TO_VARIANT[bundle];
  }
  Sentry.addBreadcrumb({
    message: `Unsupported bundle: ${bundle} defaulting to free`,
    level: 'error',
    type: 'error',
  });
  return ContentLabPlanBundle.FREE;
};

// Older bundles have lower tiers than the new ones
// So we allow these users to upgrade no matter what
export const legacyBundles = [
  ContentLabPlanBundle.STUDIO,
  ContentLabPlanBundle.BUSINESS,
  ContentLabPlanBundle.FREE,
];

export const isFreePlan = (subscription?: ContentLabSubscription) =>
  getPlanVariant(subscription) === ContentLabPlanBundle.FREE;

export const isTrialPlan = (subscription?: ContentLabSubscription) =>
  getPlanVariant(subscription) === ContentLabPlanBundle.TRIAL;

export const isCancelledTrialPlan = (subscription?: ContentLabSubscription) =>
  isTrialPlan(subscription) && !subscription?.isAutoRenewEnabled;

export function getFeaturesTitle(bundle: ContentLabPlanBundle): string {
  const map: Partial<Record<ContentLabPlanBundle, string>> = {
    [ContentLabPlanBundle.BUSINESS]: translate('Includes everything, plus:', {
      plan: translate('Starter'),
    }),
    [ContentLabPlanBundle.ENTERPRISE]: translate('Includes everything, plus:', {
      plan: translate('Pro'),
    }),
  };
  return map[bundle] || translate('Features:');
}

export function getPlanFeatures(bundle: ContentLabPlanBundle): string[] {
  if (bundle === ContentLabPlanBundle.ENTERPRISE) {
    return [
      translate('Custom number of credits'),
      translate('Flexible number of seats'),
      translate('Advanced data protection'),
      translate('Early access to new features'),
      translate('Premium support'),
      translate('Dedicated Customer Success Manager'),
    ];
  }
  if (bundle === ContentLabPlanBundle.PRO) {
    return [
      translate('Faster processing'),
      translate('Translate to 175+ languages'),
      translate('Unlimited project storage'),
      translate('Batch video generation'),
      translate('Priority support'),
    ];
  }
  return [
    translate('No watermark'),
    bundle === ContentLabPlanBundle.STARTER
      ? ''
      : translate('Unlimited viewers'),
    translate('Basic support'),
  ].filter((str) => str !== '');
}

export function getPriceCurrency(
  currency: string | undefined,
  price: number | undefined
): string {
  const actualPrice = price || 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || DEFAULT_CURRENCY,
    maximumFractionDigits: Number.isInteger(actualPrice) ? 0 : 2,
  }).format(actualPrice);
}

export function getBundleTranslatedName(bundle: ContentLabPlanBundle): string {
  switch (bundle) {
    case ContentLabPlanBundle.FREE:
      return translate('free');
    case ContentLabPlanBundle.STUDIO:
      return translate('studio');
    case ContentLabPlanBundle.BUSINESS:
      return translate('business');
    case ContentLabPlanBundle.ENTERPRISE:
      return translate('enterprise');
    case ContentLabPlanBundle.PRO:
      return translate('pro');
    case ContentLabPlanBundle.STARTER:
      return translate('starter');
    case ContentLabPlanBundle.TRIAL:
      return translate('free trial');
    default:
      return translate('free');
  }
}

function convertDuration(isoDuration: string): Duration | null {
  if (isoDuration === 'P1Y') return Duration.YEARLY;
  if (isoDuration === 'P1M') return Duration.MONTHLY;
  if (isoDuration === 'P1000Y') return Duration.ETERNITY;
  console.error(`Unsupported duration: ${isoDuration}`);
  return null;
}

export function convertBundle(bundle: string): ContentLabPlanBundle | null {
  switch (bundle) {
    case 'studio':
      return ContentLabPlanBundle.STUDIO;
    case 'business':
      return ContentLabPlanBundle.BUSINESS;
    case 'free':
      return ContentLabPlanBundle.FREE;
    case 'enterprise':
      return ContentLabPlanBundle.ENTERPRISE;
    case 'pro':
      return ContentLabPlanBundle.PRO;
    case 'starter':
      return ContentLabPlanBundle.STARTER;
    case 'trial':
      return ContentLabPlanBundle.TRIAL;
    default:
      console.error(`Unsupported bundle: ${bundle}`);
      return null;
  }
}

export function splitPlansByDuration(plans: SubscriptionPlan[]) {
  const yearlyPlans: SubscriptionPlan[] = [];
  const monthlyPlans: SubscriptionPlan[] = [];
  plans.forEach((plan) => {
    if (plan.duration === Duration.YEARLY) {
      yearlyPlans.push(plan);
    }
    if (plan.duration === Duration.MONTHLY) {
      monthlyPlans.push(plan);
    }
  });
  return {yearlyPlans, monthlyPlans};
}

function sortPlansByTier(plans: SubscriptionPlan[]) {
  return plans.sort((a: SubscriptionPlan, b: SubscriptionPlan) =>
    a.tier && b.tier ? b.tier - a.tier : b.tier || a.tier
  );
}

function groupPlansByBundle(plans: SubscriptionPlan[]) {
  let starterPlans: SubscriptionPlan[] = [];
  let proPlans: SubscriptionPlan[] = [];

  plans.forEach((plan: SubscriptionPlan) => {
    if (plan.bundle === ContentLabPlanBundle.STARTER) starterPlans.push(plan);
    else if (plan.bundle === ContentLabPlanBundle.PRO) proPlans.push(plan);
  });

  starterPlans = sortPlansByTier(starterPlans);
  proPlans = sortPlansByTier(proPlans);

  return {starterPlans, proPlans};
}

export function convertPlansDataToPlansCollections(
  plansData: ContentLabPlan[],
  activeSubscription: ContentLabPlan | undefined
): {plansCollections: PlansCollection[]; activePlan?: SubscriptionPlan} {
  const plans: SubscriptionPlan[] = [];
  let activePlan: SubscriptionPlan | undefined;

  plansData.forEach((plan: ContentLabPlan) => {
    if (plan.quoteDetails) {
      const planBundle = convertBundle(plan.quoteDetails.bundle);
      const planDuration = convertDuration(plan.duration);

      if (
        planDuration === Duration.ETERNITY ||
        planDuration === null ||
        planBundle === null
      ) {
        return;
      }

      const mappedPlan = {
        id: plan.griffinQuoteId,
        price: planDuration === Duration.YEARLY ? plan.price / 12 : plan.price,
        yearlyPrice: planDuration === Duration.YEARLY ? plan.price : null,
        currency: plan.currency,
        isDisabled:
          activeSubscription !== undefined &&
          plan.tier > activeSubscription.tier &&
          !legacyBundles.includes(
            convertBundle(activeSubscription.quoteDetails.bundle) ??
              ContentLabPlanBundle.FREE
          ),
        bundle: planBundle,
        tier: plan.tier,
        duration: planDuration,
        videos: getPlanTokenQuantity(plan) ?? 0,
        editors: plan.quoteDetails.editors ?? 0,
      };
      if (plan.griffinQuoteId === activeSubscription?.griffinQuoteId) {
        activePlan = mappedPlan;
      }
      plans.push(mappedPlan);
    }
  });

  const {starterPlans, proPlans} = groupPlansByBundle(plans);
  const studioCollection: PlansCollection = {
    id: ContentLabPlanBundle.STARTER.valueOf(),
    bundle: ContentLabPlanBundle.STARTER,
    isPromoted: false,
    plans: starterPlans,
  };

  const businessCollection: PlansCollection = {
    id: ContentLabPlanBundle.PRO.valueOf(),
    bundle: ContentLabPlanBundle.PRO,
    isPromoted: true,
    plans: proPlans,
  };

  const enterpriseCollection: PlansCollection = {
    id: ContentLabPlanBundle.ENTERPRISE.valueOf(),
    bundle: ContentLabPlanBundle.ENTERPRISE,
    isPromoted: false,
    plans: [],
  };
  return {
    plansCollections: [
      studioCollection,
      businessCollection,
      enterpriseCollection,
    ],
    activePlan,
  };
}

export function saveSessionIdsToStorage(
  productId: string,
  processId: string
): void {
  const flow = AnalyticsService.getFlow(FLOW_NAMES.CONTENT_STUDIO.PRICING);
  setContentLabWebPaymentFlowId(flow?.flow_id);
  setContentLabWebPaymentIds({
    flowId: flow?.flow_id,
    processId,
    presentationId: flow?.screen_presentation_id,
    productId,
  });
}
